import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  useListJobNotifyRecipientsQuery,
  useAddJobNotifyRecipientMutation,
  useDeleteJobNotifyRecipientMutation,
} from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

const inputStyle = {
  width: '100%', padding: '6px 10px', border: `1px solid ${brand.softGray}`,
  borderRadius: 6, fontSize: '0.85rem', color: brand.deepBlue, outline: 'none', background: 'white',
};

// One recipient list for a given notify_type. Add/delete go through the parent's
// mutations; the parent owns the (shared) query and refetch.
function RecipientList({ title, description, icon, notifyType, recipients, onAdd, onDelete, adding }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const cleaned = email.trim();
    if (!cleaned) return;
    try {
      await onAdd({ notify_type: notifyType, email: cleaned });
      setEmail('');
    } catch (err) {
      setError(err?.data?.detail || 'Could not add recipient.');
    }
  };

  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.35rem' }}>
        <h6 style={{ color: brand.deepBlue, fontWeight: 700, margin: 0 }}>
          <i className={`bi bi-${icon} me-2`} />{title}
        </h6>
        <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>{description}</span>
      </div>

      <form onSubmit={submit} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@prosperitycapitaladvisors.com"
          style={{ ...inputStyle, maxWidth: 360 }}
        />
        <button
          type="submit"
          disabled={adding || !email.trim()}
          style={{
            background: brand.deepBlue, color: 'white', border: 'none', borderRadius: 6,
            padding: '6px 14px', fontWeight: 600, fontSize: '0.85rem',
            opacity: (adding || !email.trim()) ? 0.7 : 1,
          }}
        >
          <i className="bi bi-plus-lg me-1" />Add
        </button>
        {error && (
          <span style={{ color: '#b00020', fontSize: '0.85rem' }}>
            <i className="bi bi-exclamation-triangle me-1" />{error}
          </span>
        )}
      </form>

      {recipients.length === 0 ? (
        <div style={{ color: brand.charcoal, fontSize: '0.85rem', padding: '0.25rem 0' }}>
          No recipients yet — add one above.
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {recipients.map((r) => (
            <span
              key={r.id}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
                background: 'white', border: `1px solid ${brand.softGray}`, borderRadius: 999,
                padding: '0.25rem 0.6rem', fontSize: '0.82rem', color: brand.deepBlue,
              }}
            >
              <i className="bi bi-envelope" /> {r.email}
              <button
                onClick={() => onDelete(r)}
                title="Remove"
                style={{ background: 'none', border: 'none', color: '#b00020', padding: 0, lineHeight: 1 }}
              >
                <i className="bi bi-x-circle-fill" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
RecipientList.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  icon: PropTypes.string.isRequired,
  notifyType: PropTypes.string.isRequired,
  recipients: PropTypes.array.isRequired,
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  adding: PropTypes.bool,
};

// HR-configured notification recipient lists for job openings:
//   application - emailed automatically when an employee submits an application
//   posting     - emailed when HR manually announces a new posting (from the
//                 Job Openings page)
export default function JobNotificationsSection() {
  const { data, isLoading, isError, refetch } = useListJobNotifyRecipientsQuery({});
  const [addRecipient, addState] = useAddJobNotifyRecipientMutation();
  const [deleteRecipient] = useDeleteJobNotifyRecipientMutation();

  const all = useMemo(() => data?.recipients || [], [data]);
  const applicationList = all.filter((r) => r.notify_type === 'application');
  const postingList = all.filter((r) => r.notify_type === 'posting');

  // Tags should refetch the list, but don't rely on tag config — refetch after
  // each write so the UI is always correct.
  const onAdd = async (body) => {
    await addRecipient(body).unwrap();
    refetch();
  };
  const onDelete = async (r) => {
    if (!window.confirm(`Remove ${r.email} from this notification list?`)) return;
    try {
      await deleteRecipient({ id: r.id }).unwrap();
      refetch();
    } catch (err) {
      window.alert(err?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden', marginBottom: '1.5rem' }}>
      <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem' }}>
        <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
          <i className="bi bi-bell me-2" />Job Opening Notifications
        </h5>
      </div>

      <div style={{ padding: '1.25rem' }}>
        {isLoading ? (
          <div style={{ padding: '1.5rem', textAlign: 'center' }}>
            <div className="spinner-border" style={{ color: brand.tealBlue }} />
          </div>
        ) : isError ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: brand.charcoal }}>
            Unable to load notification recipients.
          </div>
        ) : (
          <>
            <RecipientList
              title="Application submitted"
              description="Emailed automatically when an employee submits an application."
              icon="inbox"
              notifyType="application"
              recipients={applicationList}
              onAdd={onAdd}
              onDelete={onDelete}
              adding={addState.isLoading}
            />
            <hr style={{ borderColor: brand.softGray }} />
            <RecipientList
              title="New posting added"
              description="Emailed when HR manually announces a posting from the Job Openings page."
              icon="megaphone"
              notifyType="posting"
              recipients={postingList}
              onAdd={onAdd}
              onDelete={onDelete}
              adding={addState.isLoading}
            />
          </>
        )}
      </div>
    </div>
  );
}
